import {ErrorFlow, Brick, registerBrick} from 'olympe';
import {formatDateYYYYMMDD, getDateInCurrentTimezone} from '../date';
import {accountIsBlankOrUnknown} from '../../components/_helpers';
import {getValueFromLabelValue} from '../checkObjects';
import {tagToDisplayER} from '../tagTransforms';
import {normalizeDecimalSeparator} from '../number';
import {EMPTY_MATERIAL_NUMBER, getStringValueForSourceFromApiKey} from '../constants';

/**
 * Create the JSON to send to the Send PR API, from PR JSON
 */
export default class FormatPR extends Brick {
    /**
     * Executed every time an input (pRFromForm) gets updated.
     * Note that this method will _not_ be executed if an input value is undefined.
     *
     * @protected
     * @param {BrickContext} context
     * @param {string} pRFromForm
     * @param {string} prTag
     * @param {boolean} isTestRun
     * @param {function(string)} setPRToAPI
     * @param {function(*)} setErrorFlow
     */
    update(context, [pRFromForm, prTag, isTestRun], [setPRToAPI, setErrorFlow]) {
        let parsedPR;
        try {
            parsedPR = JSON.parse(pRFromForm);
        } catch (e) {
            console.error(e);
            return;
        }

        const createMaterialPayload = (pr) => {
            const submissionDate = getDateInCurrentTimezone(new Date());
            const requestedDeliveryDate = getDateInCurrentTimezone(new Date(pr.headerDetails.defaultRequestedDeliveryDate));
            return pr.materials.map((_, idx) => createPayloadObject(pr, idx, submissionDate, requestedDeliveryDate));
        };

        const prItemNo = (pr, idx) => {
            return ((pr.materials[idx].id + 1) * 10).toLocaleString('en-US', {
                minimumIntegerDigits: 5,
                useGrouping: false,
            });
        };

        const getAccountsForMaterial = (pr, idx) => {
            const matId = pr.materials[idx].id;
            return pr.financialInfo.areAccountSplitByMaterial ? pr.financialInfo.accountDetailsByMaterial.find((a) => a.material_id === matId).accounts : pr.financialInfo.accounts;
        };

        const getDistribution = (pr, idx, isAccountBlankOrUnknown) => {
            // If Distribution =1 -> Quantity field to be filled and taken into account
            // If Distribution =2 -> Percentage field
            // If Distribution =3 -> NetPrice
            const distributionByAllocationType = {
                quantity: 1,
                percentage: 2,
                price: 3,
            };
            const accounts = getAccountsForMaterial(pr, idx);

            if (accounts.length > 1 && !isAccountBlankOrUnknown) {
                const allocationType = accounts[0]?.allocationType;

                return allocationType && distributionByAllocationType[allocationType] !== undefined ?
                    distributionByAllocationType[allocationType] :
                    '';
            } else {
                return '';
            }
        };

        const createItemDistributionPayload = (pr, idx, isAccountBlankOrUnknown) => {
            const accounts = getAccountsForMaterial(pr, idx) || [];
            return {
                'results': accounts.map((account, index) => {
                    const accountTypeValue = getValueFromLabelValue(account.accountType);
                    // If accountType is blank or unknown, do not read the account number
                    const costCenter = (accountTypeValue === 'S' || accountTypeValue === 'K') ? isAccountBlankOrUnknown ? '' : getValueFromLabelValue(account.costCenter) : '';
                    const wbs = (accountTypeValue === 'S' || accountTypeValue === 'P') ? isAccountBlankOrUnknown ? '' : getValueFromLabelValue(account.wbs) : '';
                    const profitCenter = accountTypeValue === '2' ? isAccountBlankOrUnknown ? '' : getValueFromLabelValue(account.profitCenter) : '';
                    const GlAcc = isAccountBlankOrUnknown ? '' : getValueFromLabelValue(account.glNumber);

                    const quantity = !isAccountBlankOrUnknown && account.allocationType === 'quantity' ? account.allocateFor : '';
                    const percentage = !isAccountBlankOrUnknown && account.allocationType === 'percentage' ? account.allocateFor : '';
                    const netPrice = !isAccountBlankOrUnknown && account.allocationType === 'price' ? account.allocateFor : '';

                    return {
                        'PrNumber': parsedPR.prNumber ? parsedPR.prNumber.toString() : '',
                        'PrItemNo': prItemNo(pr, idx),
                        'PrSrlNoAcAs': `${(index + 1)}`,
                        'CostCenter': costCenter,
                        'GlAcc': GlAcc,
                        'WbsElement': wbs,
                        'ProfitCentr': profitCenter,
                        'Quantity': quantity ? quantity.toString() : '',
                        'Percentage': percentage ? percentage.toString() : '',
                        'NetPrice': netPrice ? netPrice.toString() : '',
                    };
                }),
            };
        };

        const generateConditionsPayload = (pr, idx) => {
            const conditions = pr.materials[idx].transportCostConditions;
            const material = pr.materials[idx];

            if (!conditions) return {results: []};

            const calculateTotal = (condition, material) => {
                if (condition.perUnit && condition.amount) {
                    return String((Number(material.quantity) / Number(condition.perUnit) ) * Number(normalizeDecimalSeparator(condition.amount)) * Number(condition.currencyExchangeRate));
                }
                if ( condition.amount ) {
                    return String(Number(normalizeDecimalSeparator(condition.amount)) * Number(condition.currencyExchangeRate));
                }
                if (condition.percentage && material) {
                    return String(((material.price * material.quantity) / 100) * Number(condition.percentage) * Number(condition.currencyExchangeRate));
                }
                return '';
            };

            const generatedPayload = conditions.map((condition)=>{
                return {
                    'PrNumber': condition.eprId,
                    'PrItemNo': prItemNo(pr, idx),
                    'ConType': condition.conditionType?.value,
                    'ConTyLable': condition.conditionType?.label.replace(condition.conditionType?.value, '').replace(' - ', ''),
                    'ConCurren': condition.conditionCurrency?.value,
                    'ConAmount': normalizeDecimalSeparator(condition.amount),
                    'ConPercen': condition.percentage,
                    'ConPricPerUnit': condition.perUnit,
                    'ConTotalValue': calculateTotal(condition, material),
                    'UnitOfMea': condition.unit,
                    'ConSupplier': String(condition.conditionSupplier?.supplierCode),
                };
            });
            return {results: [...generatedPayload]};
        };

        const createPayloadObject = (pr, idx, submissionDate, requestedDeliveryDate) => {
            // Gather account's infos
            const accountTypeValue = getValueFromLabelValue(pr.financialInfo.areAccountSplitByMaterial ?
                pr.financialInfo.accountDetailsByMaterial[idx].accounts[0].accountType :
                pr.financialInfo.accounts[0].accountType);
            const wbs = (accountTypeValue === 'P' || accountTypeValue === 'S') ? pr.financialInfo.areAccountSplitByMaterial ?
                pr.financialInfo.accountDetailsByMaterial[idx].accounts[0].wbs :
                pr.financialInfo.accounts[0].wbs : '';
            const costCenter = (accountTypeValue === 'K' || accountTypeValue === 'S') ? pr.financialInfo.areAccountSplitByMaterial ?
                pr.financialInfo.accountDetailsByMaterial[idx].accounts[0].costCenter :
                pr.financialInfo.accounts[0].costCenter : '';
            const profitCenter = accountTypeValue === '2' ? pr.financialInfo.areAccountSplitByMaterial ?
                pr.financialInfo.accountDetailsByMaterial[idx].accounts[0].profitCenter :
                pr.financialInfo.accounts[0].profitCenter : '';
            const glNumber = pr.financialInfo.areAccountSplitByMaterial ?
                pr.financialInfo.accountDetailsByMaterial[idx].accounts[0].glNumber :
                pr.financialInfo.accounts[0].glNumber;
            const isAccountBlankOrUnknown = accountIsBlankOrUnknown(accountTypeValue);

            // check about timezones
            const requestedDeliveryDateMaterial = pr.materials[idx].requestedDeliveryDate ? getDateInCurrentTimezone(new Date(pr.materials[idx].requestedDeliveryDate)) :requestedDeliveryDate;

            // If there is an OTV or MTV, format the One Time Vendor information
            let otv = '';
            let mtv = '';

            if (pr.oneTimeVendor?.supplierName) {
                const {houseNumber, street, city, state, country, postalCode, name2} =
                    pr.oneTimeVendor.address;
                const phoneNumber = pr.oneTimeVendor.phoneNumber;
                const supplierName = pr.vendorName;

                if (pr.oneTimeVendor.supplierType === 'MTV') {
                    const streetWithoutComma = (street ?? '').replace(/,/g, '');

                    mtv =
                        `SupplierName:${supplierName}, House no:${houseNumber}, ` +
                        `Street:${streetWithoutComma}, City:${city}, State:${state}, ` +
                        `Country:${country}, PIN:${postalCode}, Telephone:${phoneNumber || ''}, ` +
                        `SupplierName2:${name2 || ''},`;
                } else {
                    otv =
                        `SupplierName:${supplierName}, House no:${houseNumber}, ` +
                        `Street:${street}, City:${city}, State:${state}, ` +
                        `Country:${country}, PIN:${postalCode}, Telephone:${phoneNumber || ''}, ` +
                        `SupplierName2:${name2 || ''},`;
                }
            }

            const isOTVorMTV = Boolean(otv || mtv);

            const wayOfBuying = getValueFromLabelValue(parsedPR.wayOfBuying.currentValue);

            let prFrom = pr.sourceToDisplay || getStringValueForSourceFromApiKey(pr.source, pr.sourceFromApiKey);
            if (prFrom.length > 25) {
                if (prFrom === 'Sourcing Event (Agency Mania)') {
                    prFrom = 'Sourcing Event (AM)';
                } else if (prFrom === 'Non-PSL (Describe Your Need)') {
                    prFrom = 'Non-PSL';
                } else {
                    prFrom = prFrom.substring(0, 25);
                }
            }

            const materialNumber = getValueFromLabelValue(pr.materials[idx].name) === EMPTY_MATERIAL_NUMBER ? '' : getValueFromLabelValue(pr.materials[idx].name);

            let commonInfo= {
                AccAssignmentCat: accountTypeValue === 'S' ? 'P' : accountTypeValue,
                Compliance_Ind: pr.attachmentsInfo.complianceIndicator.charAt(0),
                ContractNo: pr.contractID ? pr.contractID.toString().substring(0, 15) : '',
                CostCenter: isAccountBlankOrUnknown ? '' : getValueFromLabelValue(costCenter),
                CostPlan: pr.materials[idx].ppmCostPlan || '',
                Covr_Sht: pr.sendingOptions.coversheet,
                Currency: getValueFromLabelValue(pr.materials[idx].currency),
                DelivDate: formatDateYYYYMMDD(requestedDeliveryDateMaterial).replace(/-/gi, ''),
                Distribution: getDistribution(pr, idx, isAccountBlankOrUnknown).toString(),
                Evaluate_Rcpt_Stmnt: wayOfBuying !== 'FO' && pr.headerDetails.ers ? 'X' : '',
                ExtReqNo: 'EPR-' + (pr.EPRId || tagToDisplayER(prTag)),
                Free_Item: pr.materials[idx].freeItem ? 'X' : '',
                GlAcc: isAccountBlankOrUnknown ? '' : getValueFromLabelValue(glNumber),
                Goods_Recpt: wayOfBuying !== 'FO' && pr.headerDetails.goodsReceipt ? 'X' : '',
                GR_Bsd_IV: wayOfBuying !== 'FO' && pr.headerDetails.grBasedInvoiceVerification ? 'X' : '',
                HeaderNote: pr.internalComment,
                HeaderText: pr.externalComment,
                Hide_Price: pr.sendingOptions.hidePrice ? 'X' : '',
                Incoterms: (() => {
                    const incoterm = pr.headerDetails.incoterms;

                    if (!incoterm.manuallyModified) return '';
                    if (!incoterm.data) return '';

                    const value = incoterm.data.value || '';
                    const label = incoterm.data.label || '';

                    return value ? `${value} ${label}`.trim() : '';
                })(),
                Invce_Prtnr: isOTVorMTV ? '' : getValueFromLabelValue(pr.headerDetails.invoicePartner),
                Item_note: pr.materials[idx].internalComments,
                Item_text: pr.materials[idx].supplierComments,
                ItemCat: '0',
                ItemText: '',
                Language: 'EN',
                Material: materialNumber,
                MaterialGroup: getValueFromLabelValue(pr.materials[idx].l3Category),
                Non_Psl: pr.nonPslJustification || '',
                Ordr_Addrs: isOTVorMTV ? '' : getValueFromLabelValue(pr.headerDetails.orderingAddress),
                OTV_Addrs: otv,
                MTV_Addrs: mtv,
                Plant: getValueFromLabelValue(pr.materials[idx].plant),
                PR_From: prFrom,
                PR_From_Item: pr.materials[idx].source?.value,
                PriceUnit: pr.materials[idx].per != null ?
                    (pr.materials[idx].freeItem ? Math.round(pr.materials[idx].per).toString() : pr.materials[idx].per).toString() :
                    '',
                PrItemNo: prItemNo(pr, idx),
                PrItemToItemDistribution: createItemDistributionPayload(pr, idx, isAccountBlankOrUnknown),
                Pr_itemCon: generateConditionsPayload(pr, idx),
                PrNumber: pr.prNumber ? pr.prNumber.toString() : '',
                ProfitCntr: isAccountBlankOrUnknown ? '' : getValueFromLabelValue(profitCenter),
                PSL_ID: (() => {
                    const pslId = pr.materials[idx].pslId;
                    const pslMaterialId = pr.materials[idx].uniqueSourcingToolId;

                    const hasPslId = pslId !== undefined && pslId !== null && pslId !== '';
                    const hasPslMaterialId = pslMaterialId !== undefined && pslMaterialId !== null && pslMaterialId !== '';

                    if (!hasPslId && !hasPslMaterialId) {
                        return '';
                    }

                    const fullValue = hasPslId ?
                        `${pslId}-${hasPslMaterialId ? pslMaterialId : ''}` :
                        '';

                    return fullValue.substring(0, 20);
                })(),
                PurchOrg: getValueFromLabelValue(pr.headerDetails.purchaseOrganization),
                PurGroup: getValueFromLabelValue(pr.headerDetails.purchasingGroup),
                Quantity: pr.materials[idx].quantity ? pr.materials[idx].quantity.toString() : '',
                ReqDate: formatDateYYYYMMDD(submissionDate).replace(/-/gi, ''),
                Requisitioner: getValueFromLabelValue(pr.spender),
                ShortText: pr.materials[idx].description.slice(0, 40),
                Sourcing_ID: pr.sourcingEventID ? pr.sourcingEventID.toString(): '',
                SPayTerm: getValueFromLabelValue(pr.headerDetails.newPaymentTerms),
                StorageLoc: getValueFromLabelValue(pr.materials[idx].storageLocation),
                Supp_Auxiliary_Id: pr.materials[idx].supplierPartAuxiliaryId?.toString()?.substring(0, 240),
                Supplier_Stats: pr.headerDetails.supplierStatus,
                TaxCode: getValueFromLabelValue(pr.materials[idx].taxCode),
                TrackingNo: '',
                Unit: getValueFromLabelValue(pr.materials[idx].unit),
                ValPrice: pr.materials[idx].price ? pr.materials[idx].price?.toString() : '',
                VendMat: pr.materials[idx].supplierPartId?.toString()?.substring(0, 35),
                Vendor: pr.vendorCode ? pr.vendorCode.toString() : '',
                WBS: isAccountBlankOrUnknown ? '' : getValueFromLabelValue(wbs),
            };

            if (pr.materials[idx].deliveryAddress?.manuallyModified) {
                commonInfo = {
                    ...commonInfo,
                    City: pr.materials[idx].deliveryAddress .city,
                    Country: pr.materials[idx].deliveryAddress.country ? pr.materials[idx].deliveryAddress.country : '',
                    House_no: pr.materials[idx].deliveryAddress.houseNumber,
                    Name: pr.materials[idx].deliveryAddress.name,
                    Name2: pr.materials[idx].deliveryAddress.name2 || '',
                    PostalCode: pr.materials[idx].deliveryAddress.postalCode,
                    State: (pr.materials[idx].deliveryAddress.state || '').substring(0, 2),
                    Street: pr.materials[idx].deliveryAddress.street,
                };
            }

            if (wayOfBuying === 'NB') { // Standard Requisition
                return commonInfo;
            } else if (wayOfBuying === 'FO') { // Framework Requisition
                const validityStart = formatDateYYYYMMDD(getDateInCurrentTimezone(new Date(pr.headerDetails.validityStart)));
                const validityEnd = formatDateYYYYMMDD(getDateInCurrentTimezone(new Date(pr.headerDetails.validityEnd)));
                return {
                    ...commonInfo,
                    Expected_Limit: pr.materials[idx].price ? pr.materials[idx].price.toString() : '',
                    ItemCat: '1',
                    ItemText: 'Validity Start: ' + validityStart + '\nValidity End: ' + validityEnd,
                    Sum_Limit: pr.materials[idx].price ? pr.materials[idx].price.toString() : '',
                };
            } else { // Capex Requisition
                return commonInfo;
            }
        };

        try {
            const payload = createMaterialPayload(parsedPR);
            setPRToAPI(JSON.stringify({
                results: {
                    PrNumber: parsedPR.prNumber ? parsedPR.prNumber.toString() : '',
                    DocType: getValueFromLabelValue(parsedPR.wayOfBuying.currentValue),
                    Test_Run: isTestRun ? 'X' : '',
                    HeaderToItem: {
                        results: payload,
                    },
                },
            }));
        } catch (e) {
            setErrorFlow(ErrorFlow.create(
                `Cannot create payload: ${e}`,
                1,
            ));
        }
    }
}

registerBrick('017c3ae4b44dc89b62f8', FormatPR);
